import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Switch,
  Button,
  IconButton,
  Chip,
  TablePagination,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from "@mui/material";
import themestyle from "../theme";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_ATTENDANCE_BY_CLASS, GET_STUDENT_ATTENDANCE_HISTORY } from "../graphql/query";
import {
  GetStudentAttendanceHistoryResponse,
  StudentAttendance,
} from "../interfaces/attendance.interface";
import SkeletonLoader from "./SkeletonLoader";
import CustomPaginationActions from "./CustomPaginationActions";
import { AttendanceDataGridProps } from "../interfaces/attendanceProps";

const AttendanceDataGrid: React.FC<AttendanceDataGridProps> = ({
  classId,
  attendanceDate,
  loading: externalLoading = false,
  searchQuery = "",
  onStudentsChange,
  readOnly = false,
  externalStudents,
  defaultStudentLabel = "Enrolled",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [students, setStudents] = useState<StudentAttendance[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const { data: attendanceData, loading: internalLoading } = useQuery(
    GET_ATTENDANCE_BY_CLASS,
    {
      variables: { classId, date: attendanceDate },
      skip: !classId,
      fetchPolicy: "network-only",
    },
  );
  const [fetchStudentHistory, { data: historyData, loading: historyLoading }] =
    useLazyQuery<GetStudentAttendanceHistoryResponse>(GET_STUDENT_ATTENDANCE_HISTORY, {
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    if (externalStudents) {
      setStudents(externalStudents);
    }
  }, [externalStudents]);

  useEffect(() => {
    if (!externalStudents && attendanceData?.getAttendanceByClass?.data?.students) {
      const loadedStudents = attendanceData.getAttendanceByClass.data.students;
      setStudents(loadedStudents);
      if (onStudentsChange) onStudentsChange(loadedStudents);
    }
  }, [attendanceData, onStudentsChange, externalStudents]);

  const loading = externalLoading || internalLoading;
  const getStudentTag = (student: StudentAttendance) => {
    if (student.sessionCategory) {
      let colors = { bg: "#FFF4E5", color: "#B45309" }; // Default Drop-In
      
      if (student.sessionCategory.includes("Trail")) {
        colors = { bg: "#F5F3FF", color: "#6D28D9" }; // Trail - Purple/Blue
      } else if (student.sessionCategory.includes("Override")) {
        colors = { bg: "#F3F4F6", color: "#374151" }; // Override - Gray
      }

      return {
        label: student.sessionCategory,
        ...colors,
      };
    }

    const label = student.passName || student.passType || defaultStudentLabel;

    return {
      label,
      bg: "#EEF7E3",
      color: "#2E7D32",
    };
  };

  const handleToggle = (id: string) => {
    if (readOnly) return;
    const updated = students.map((s) =>
      s.studentId === id ? { ...s, isPresent: !s.isPresent } : s,
    );
    setStudents(updated);
    if (onStudentsChange) onStudentsChange(updated);
  };

  const filteredStudents = useMemo(() => {
    return students.filter((s) =>
      s.studentName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [students, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const handleHistoryClick = (student: StudentAttendance) => {
    if (!classId) return;
    setSelectedStudentName(student.studentName);
    setHistoryOpen(true);
    fetchStudentHistory({
      variables: {
        classId,
        studentId: student.studentId,
      },
    });
  };

  const historyPayload = historyData?.getStudentAttendanceHistory?.data;
  const historyRows = historyPayload?.history || [];

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: themestyle.colors.white,
        position: "relative",
        minHeight: loading ? "200px" : "auto",
      }}
    >
      <Box
        sx={{
          maxHeight: isMobile ? "auto" : "480px",
          overflowY: isMobile ? "visible" : "auto",
        }}
      >
        {filteredStudents.length > 0 ? (
          filteredStudents
            .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
            .map((student) => (
              <Box
                key={student.studentId}
                sx={{
                  p: isMobile ? 2 : 2,
                  px: isMobile ? 2 : 3,
                  borderBottom: "1px solid #F3F4F6",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                {isMobile ? (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            fontSize: "14px",
                            color: themestyle.colors.black,
                            fontFamily: themestyle.fontFamily.Roboto
                          }}
                        >
                          {student.studentName} {student.studentCustomId ? `– ${student.studentCustomId}` : ""}
                        </Typography>
                        <Chip
                          label={getStudentTag(student).label}
                          size="small"
                          sx={{
                            mt: 0.75,
                            backgroundColor: getStudentTag(student).bg,
                            color: getStudentTag(student).color,
                            fontWeight: 700,
                            fontSize: "10px",
                            height: "22px",
                            borderRadius: "8px",
                            "& .MuiChip-label": {
                              px: 1,
                            },
                          }}
                        />
                      </Box>
                      <Switch
                        size="small"
                        checked={student.isPresent}
                        disabled={readOnly}
                        onChange={() => handleToggle(student.studentId)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color:themestyle.colors.activeGreen,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor:themestyle.colors.activeGreen,
                            },
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Chip
                        label={student.isPresent ? "PRESENT" : "ABSENT"}
                        sx={{
                          backgroundColor: student.isPresent
                            ? "#EEF9F1"
                            : "#FEF2F2",
                          color: student.isPresent ? "#10B981" : "#EF4444",
                          fontWeight: 700,
                          fontSize: "10px",
                          borderRadius: "6px",
                          height: "22px",
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => handleHistoryClick(student)}
                        sx={{
                          textTransform: "none",
                          color: "#374151",
                          borderColor: "#E5E7EB",
                          borderRadius: "8px",
                          fontSize: "11px",
                          px: 3,
                          py: 0.5,
                          fontWeight: 600,
                        }}
                      >
                        History
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{
                          fontSize: "16px",
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                         {student.studentName} {student.studentCustomId ? `– ${student.studentCustomId}` : ""}
                      </Typography>
                      <Chip
                        label={getStudentTag(student).label}
                        size="small"
                        sx={{
                          mt: 0.75,
                          alignSelf: "flex-start",
                          backgroundColor: getStudentTag(student).bg,
                          color: getStudentTag(student).color,
                          fontWeight: 700,
                          fontSize: "11px",
                          height: "24px",
                          borderRadius: "8px",
                          "& .MuiChip-label": {
                            px: 1,
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <Chip
                        label={student.isPresent ? "PRESENT" : "ABSENT"}
                        sx={{
                          backgroundColor: student.isPresent
                            ? "#EEF9F1"
                            : "#FEF2F2",
                          color: student.isPresent ? "#10B981" : "#EF4444",
                          fontWeight: 700,
                          fontSize: "12px",
                          borderRadius: "20px",
                          width: 100,
                        }}
                      />
                      <Switch
                        checked={student.isPresent}
                        disabled={readOnly}
                        onChange={() => handleToggle(student.studentId)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#A3E635",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#A3E635",
                            },
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => handleHistoryClick(student)}
                        sx={{
                          textTransform: "none",
                          color: "#374151",
                          borderColor: "#E5E7EB",
                          borderRadius: "8px",
                          fontSize: "13px",
                          px: 2,
                        }}
                      >
                        History
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            ))
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ color: "#6B7280" }}>No students found</Typography>
          </Box>
        )}
      </Box>
      {filteredStudents.length > 0 && (
        <React.Fragment>
          {isMobile ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                py: 2,
                px: 2,
                backgroundColor: themestyle.colors.white,
                borderTop: "1px solid #F3F4F6",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "14px",
                  color: "#1F2937",
                  fontWeight: 500,
                  mr: 2,
                }}
              >
                {page * rowsPerPage + 1} -{" "}
                {Math.min((page + 1) * rowsPerPage, filteredStudents.length)}
                <span
                  style={{
                    marginLeft: "8px",
                    marginRight: "8px",
                    color: "#9CA3AF",
                  }}
                >
                  of
                </span>
                {filteredStudents.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  sx={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#1F2937",
                    "&:disabled": {
                      color: "#D1D5DB",
                      borderColor: "#F3F4F6",
                    },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 12L6 8L10 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * rowsPerPage >= filteredStudents.length}
                  sx={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#1F2937",
                    "&:disabled": {
                      color: "#D1D5DB",
                      borderColor: "#F3F4F6",
                    },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 4L10 8L6 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Box>
            </Box>
          ) : (
            <TablePagination
              component="div"
              count={filteredStudents.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20]}
              ActionsComponent={CustomPaginationActions}
              sx={{
                borderTop: "1px solid #E5E7EB",
                "& .MuiTablePagination-root": {
                  alignItems: "center",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontSize: "13px",
                    color: "#6B7280",
                  },
                "& .MuiTablePagination-displayedRows": {
                  marginRight: "16px",
                },
                "& .MuiTablePagination-actions": {
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  ml: 2,
                },
              }}
            />
          )}
        </React.Fragment>
      )}

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedStudentName || "Student"} Attendance History
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  mb: 2,
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                }}
              >
                <Box>
                  <Box sx={{ p: 2, borderRadius: "10px", backgroundColor: "#F3F9F4", textAlign: "center" }}>
                    <Typography sx={{ fontSize: "32px", fontWeight: 700, color: "#4BAF50" }}>
                      {historyPayload?.totalSessions ?? 0}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#666" }}>TOTAL SESSIONS</Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ p: 2, borderRadius: "10px", backgroundColor: "#EDF6FF", textAlign: "center" }}>
                    <Typography sx={{ fontSize: "32px", fontWeight: 700, color: "#1C86C8" }}>
                      {historyPayload?.presentCount ?? 0}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#666" }}>PRESENT</Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ p: 2, borderRadius: "10px", backgroundColor: "#FEF1F1", textAlign: "center" }}>
                    <Typography sx={{ fontSize: "32px", fontWeight: 700, color: "#E53935" }}>
                      {historyPayload?.absentCount ?? 0}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#666" }}>ABSENT</Typography>
                  </Box>
                </Box>
              </Box>

              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Recent Attendance (This Class Only)</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 2,
                  mb: 1,
                  color: "#888",
                  fontWeight: 700,
                }}
              >
                <Typography>DATE</Typography>
                <Typography>CLASS NAME</Typography>
                <Typography>STATUS</Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              {historyRows.length === 0 ? (
                <Typography sx={{ color: "#6B7280", py: 1 }}>No attendance history found.</Typography>
              ) : (
                historyRows.map((row, idx) => (
                  <Box
                    key={`${row.date}-${idx}`}
                    sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, py: 1 }}
                  >
                    <Typography>
                      {new Date(`${row.date}T00:00:00`).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </Typography>
                    <Typography>{row.className}</Typography>
                    <Typography sx={{ color: row.isPresent ? "#4CAF50" : "#E53935", fontWeight: 700 }}>
                      {row.isPresent ? "Present" : "Absent"}
                    </Typography>
                  </Box>
                ))
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="outlined" onClick={() => setHistoryOpen(false)} sx={{ textTransform: "none" }}>
                  Done
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            borderRadius: "12px",
          }}
        >
          <CircularProgress color="success" />
        </Box>
      )}
    </Box>
  );
};
export default AttendanceDataGrid;
